import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
  Show,
  SignIn,
  SignOutButton,
  SignUp,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { setHostTokenProvider } from "./lib/api";
import { createResilientClerkTokenGetter, waitForClerkSessionToken } from "./lib/clerkSession";
import { AppShell } from "./components/AppShell";
import { Button } from "@/components/ui/button";
import { personalAccountId } from "@shared/account";

// VITE_CLERK_DISABLED is a local/browser-test convenience. `.env.local` is
// loaded by Vite production builds too, so never let that flag disable Clerk
// in a deployed bundle; production reads its publishable key from /api/config.
const clerkExplicitlyDisabled = import.meta.env.DEV && import.meta.env.VITE_CLERK_DISABLED === "1";
const buildTimePublishableKey = clerkExplicitlyDisabled
  ? undefined
  : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

type AuthRuntime = {
  loaded: boolean;
  authMode: string;
  publishableKey?: string | undefined;
};

const AuthRuntimeContext = createContext<AuthRuntime>({
  loaded: clerkExplicitlyDisabled || Boolean(buildTimePublishableKey),
  authMode: buildTimePublishableKey ? "clerk" : "dev",
  publishableKey: buildTimePublishableKey,
});

const TenantKeyContext = createContext("development");
const HostTokenReadyContext = createContext(true);

export function useAuthRuntime(): AuthRuntime {
  return useContext(AuthRuntimeContext);
}

export function useClerkConfigured(): boolean {
  return Boolean(useAuthRuntime().publishableKey);
}

/** @deprecated Prefer useClerkConfigured() so runtime Worker config is included. */
export const clerkConfigured = Boolean(buildTimePublishableKey);

const clerkAppearance = {
  variables: {
    colorBackground: "#111214",
    colorForeground: "#f0f1ed",
    colorPrimary: "#b7f43b",
    colorMutedForeground: "#a3a59f",
    colorNeutral: "#f0f1ed",
    colorInputBackground: "#0d0e10",
    colorInputText: "#f0f1ed",
    fontFamily: '"Geist Variable", sans-serif',
    borderRadius: "0.625rem",
  },
  elements: {
    card: { border: "1px solid #292b2f", boxShadow: "0 24px 70px rgba(0, 0, 0, 0.52)" },
    cardBox: { boxShadow: "none" },
    footer: { background: "#111214" },
    footerActionText: { color: "#a3a59f" },
    formFieldInput: {
      background: "#0b0d10",
      borderColor: "#303238",
      color: "#f0f1ed",
      caretColor: "#b7f43b",
    },
    userButtonAvatarBox: { width: "2.5rem", height: "2.5rem" },
    userButtonPopoverCard: {
      background: "#111214",
      border: "1px solid #292b2f",
      boxShadow: "0 18px 50px rgba(0, 0, 0, 0.52)",
    },
    userButtonPopoverActionButton: { color: "#f0f1ed" },
    userButtonPopoverFooter: { background: "#111214", borderTop: "1px solid #292b2f" },
  },
};

export function AppAuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthRuntime>(() => ({
    loaded: clerkExplicitlyDisabled || Boolean(buildTimePublishableKey),
    authMode: buildTimePublishableKey ? "clerk" : "dev",
    publishableKey: buildTimePublishableKey,
  }));

  useEffect(() => {
    if (clerkExplicitlyDisabled || buildTimePublishableKey) return;
    let cancelled = false;
    fetch("/api/config")
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("config"))))
      .then((body: { authMode?: string; clerkPublishableKey?: string | null }) => {
        if (cancelled) return;
        setConfig({
          loaded: true,
          authMode: body.authMode ?? "clerk",
          publishableKey: body.clerkPublishableKey || undefined,
        });
      })
      .catch(() => {
        if (!cancelled) setConfig({ loaded: true, authMode: "unknown" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!config.loaded) return <AuthLoading />;

  const tree = (
    <AuthRuntimeContext.Provider value={config}>
      <TenantKeyGate>{children}</TenantKeyGate>
    </AuthRuntimeContext.Provider>
  );

  if (!config.publishableKey) return tree;
  return (
    <ClerkProvider
      publishableKey={config.publishableKey}
      appearance={clerkAppearance}
      afterSignOutUrl="/"
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/app"
      signUpFallbackRedirectUrl="/app"
    >
      <ClerkTokenBridge>{tree}</ClerkTokenBridge>
    </ClerkProvider>
  );
}

function TenantKeyGate({ children }: { children: ReactNode }) {
  const { publishableKey } = useAuthRuntime();
  if (!publishableKey) {
    return <TenantKeyContext.Provider value="development">{children}</TenantKeyContext.Provider>;
  }
  return <ClerkTenantKeyBridge>{children}</ClerkTenantKeyBridge>;
}

function ClerkTenantKeyBridge({ children }: { children: ReactNode }) {
  const { isLoaded, userId } = useAuth();
  return (
    <TenantKeyContext.Provider value={isLoaded && userId ? personalAccountId(userId) : "loading"}>
      {children}
    </TenantKeyContext.Provider>
  );
}

function ClerkTokenBridge({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn, sessionId, signOut } = useAuth();
  const [ready, setReady] = useState(false);
  const getTokenRef = useRef(getToken);
  const signOutRef = useRef(signOut);
  getTokenRef.current = getToken;
  signOutRef.current = signOut;
  useEffect(() => {
    setReady(false);
    setHostTokenProvider(null);
    if (!isLoaded || !isSignedIn || !sessionId) return;
    let cancelled = false;
    const recoverSession = async () => {
      if (cancelled) return;
      const current = `${window.location.pathname}${window.location.search}`;
      const signInUrl = `/sign-in?redirect_url=${encodeURIComponent(current)}`;
      try {
        await signOutRef.current({ redirectUrl: signInUrl });
      } finally {
        if (!cancelled) window.location.assign(signInUrl);
      }
    };
    const currentToken = (options?: Parameters<typeof getToken>[0]) => getTokenRef.current(options);
    void waitForClerkSessionToken(currentToken).then(async (token) => {
      if (cancelled) return;
      if (token) {
        setHostTokenProvider(createResilientClerkTokenGetter(currentToken, token, recoverSession));
        setReady(true);
        return;
      }
      await recoverSession();
    });
    return () => {
      cancelled = true;
      setHostTokenProvider(null);
    };
  }, [isLoaded, isSignedIn, sessionId]);
  return <HostTokenReadyContext.Provider value={ready}>{children}</HostTokenReadyContext.Provider>;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { loaded, publishableKey, authMode } = useAuthRuntime();
  const location = useLocation();
  const withShell = !/\/app\/sessions\/[^/]+\/studio/.test(location.pathname);
  if (!loaded) return <AuthLoading />;
  if (publishableKey) return <ClerkProtectedRoute withShell={withShell}>{children}</ClerkProtectedRoute>;
  if (authMode === "clerk") return <Navigate to="/sign-in" replace />;
  return withShell ? <AppShell>{children}</AppShell> : children;
}

function ClerkProtectedRoute({ children, withShell }: { children: ReactNode; withShell: boolean }) {
  const { isLoaded, userId } = useAuth();
  const tokenReady = useContext(HostTokenReadyContext);
  const location = useLocation();
  if (!isLoaded) return <AuthLoading />;
  if (!userId) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?redirect_url=${encodeURIComponent(redirect)}`} replace />;
  }
  if (!tokenReady) return <AuthLoading />;
  return withShell ? <AppShell>{children}</AppShell> : children;
}

export function SignInPage() {
  const { loaded, publishableKey, authMode } = useAuthRuntime();
  if (!loaded) return <AuthLoading />;
  if (!publishableKey) {
    if (authMode === "clerk") return <ClerkUnavailable />;
    return <Navigate to="/app" replace />;
  }
  return (
    <main className="auth-page">
      <Link className="auth-home" to="/">record a podcast</Link>
      <SignIn routing="path" path="/sign-in" withSignUp={false} fallbackRedirectUrl="/app" />
    </main>
  );
}

export function SignUpPage() {
  const { loaded, publishableKey, authMode } = useAuthRuntime();
  if (!loaded) return <AuthLoading />;
  if (!publishableKey) {
    if (authMode === "clerk") return <ClerkUnavailable />;
    return <Navigate to="/app" replace />;
  }
  return (
    <main className="auth-page">
      <Link className="auth-home" to="/">record a podcast</Link>
      <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" fallbackRedirectUrl="/app" />
    </main>
  );
}

function ClerkUnavailable() {
  return (
    <main className="page page-narrow auth-page">
      <Link className="auth-home" to="/">record a podcast</Link>
      <h1>Sign in</h1>
      <p className="hint">
        Host sign-in uses Clerk, but this deployment is missing <code>CLERK_PUBLISHABLE_KEY</code>.
        The studio stays locked until that key is configured.
      </p>
    </main>
  );
}

export function LandingAuthActions() {
  const { loaded, publishableKey, authMode } = useAuthRuntime();
  if (!loaded) return <span className="hint">Loading…</span>;
  if (!publishableKey) {
    return <Button asChild variant="ghost"><Link to={authMode === "clerk" ? "/sign-in" : "/app"}>Host sign in</Link></Button>;
  }
  return (
    <>
      <ClerkLoading><span className="hint">Loading…</span></ClerkLoading>
      <ClerkLoaded>
        <Show when="signed-out">
          <Button asChild><Link to="/sign-in">Sign in</Link></Button>
        </Show>
        <Show when="signed-in">
          <Button asChild><Link to="/app">Open dashboard</Link></Button>
          <UserButton />
        </Show>
      </ClerkLoaded>
    </>
  );
}

export function OpenStudioLink({ className, children }: { className?: string; children: ReactNode }) {
  const { loaded, publishableKey, authMode } = useAuthRuntime();
  if (!loaded) return <span className="hint">Loading…</span>;
  if (!publishableKey) {
    return <Link data-slot="button" className={className} to={authMode === "clerk" ? "/sign-in" : "/app"}>{children}</Link>;
  }
  return (
    <>
      <ClerkLoading><span className="hint">Loading…</span></ClerkLoading>
      <ClerkLoaded>
        <Show when="signed-out">
          <Link data-slot="button" className={className} to="/sign-in">{children}</Link>
        </Show>
        <Show when="signed-in">
          <Link data-slot="button" className={className} to="/app">{children}</Link>
        </Show>
      </ClerkLoaded>
    </>
  );
}

export function AccountAuthControls({ showIdentity = false }: { showIdentity?: boolean }) {
  if (!useClerkConfigured()) return null;
  return <ClerkAccountAuthControls showIdentity={showIdentity} />;
}

function ClerkAccountAuthControls({ showIdentity }: { showIdentity: boolean }) {
  const { user } = useUser();
  const displayName = user?.fullName || user?.username || user?.primaryEmailAddress?.emailAddress || "Account";
  const email = user?.primaryEmailAddress?.emailAddress;
  return (
    <div className={`account-auth-controls${showIdentity ? " account-auth-controls-sidebar" : ""}`}>
      <UserButton>
        <UserButton.MenuItems>
          <UserButton.Link href="/app/settings" label="Studio settings" labelIcon={<SettingsIcon />} />
        </UserButton.MenuItems>
      </UserButton>
      {showIdentity && (
        <span className="sidebar-account-copy">
          <strong>{displayName}</strong>
          {email && <small>{email}</small>}
        </span>
      )}
    </div>
  );
}

export function AccountSignOut() {
  if (!useClerkConfigured()) return <Button asChild variant="outline"><a href="/cdn-cgi/access/logout">Sign out</a></Button>;
  return <SignOutButton><Button type="button" variant="outline">Sign out</Button></SignOutButton>;
}

export function useTenantKey(): string {
  return useContext(TenantKeyContext);
}

function AuthLoading() {
  return <main className="page"><p className="hint">Loading your account…</p></main>;
}

function SettingsIcon() {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.09A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3v-4h.09A1.7 1.7 0 0 0 4.6 8.5a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.09A1.7 1.7 0 0 0 15.5 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.14.39.36.73.66 1 .3.27.68.4 1.08.4H21v4h-.09A1.7 1.7 0 0 0 19.4 15Z" /></svg>;
}
